import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChild,
  ViewChild,
  TemplateRef,
  output,
  input,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import {
  MatTable,
  MatTableDataSource,
  MatTableModule,
} from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';

import type { IBatchAnalysisRequest } from '#backend/common/IBatchAnalysisRequest';
import { TypeSafeMatCellDef } from '../../../type-safe-mat-cell-def.directive';

export interface RequestTableItem extends IBatchAnalysisRequest {
  cliente?: string;
  [key: string]: any; // Para campos adicionais específicos de cada tipo
}

export interface RequestTableAction {
  label?: string;
  icon: string;
  action: (item: RequestTableItem) => void;
  condition?: (item: RequestTableItem) => boolean;
}

type StatusCode = IBatchAnalysisRequest['status']['code'];

export interface RequestTableConfig {
  showExpandColumn: boolean;
  showActionsColumn: boolean;
  showViewAdminColumn: boolean;
  columns: string[];
  initialSort?: Sort;
  statusClassFunction?: (statusCode: StatusCode) => string;
}

@Component({
  selector: 'app-requests-table',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    TypeSafeMatCellDef,
  ],
  templateUrl: './requests-table.component.html',
  styleUrl: './requests-table.component.scss',
  providers: [provideNativeDateAdapter()],
  encapsulation: ViewEncapsulation.None,
})
export class RequestsTableComponent implements AfterViewInit {
  dataSource = input(new MatTableDataSource<RequestTableItem>([]));
  isLoading = input(false);
  length = input(0);
  config = input<RequestTableConfig>({
    showExpandColumn: true,
    showActionsColumn: true,
    showViewAdminColumn: false,
    columns: ['id', 'cliente', 'createdBy', 'createdDate', 'status'],
    initialSort: { active: 'createdDate', direction: 'desc' },
    statusClassFunction: undefined,
  });
  actions = input<RequestTableAction[]>([]);

  @ContentChild('expandedDetailTemplate')
  expandedDetailTemplate?: TemplateRef<any>;

  sortChange = output<Sort>();
  pageChange = output<PageEvent>();
  rowToggle = output<RequestTableItem>();
  viewAdminClick = output<RequestTableItem>();
  refresh = output<void>();

  @ViewChild(MatSort) declare sort: MatSort;
  @ViewChild(MatPaginator) declare paginator: MatPaginator;
  @ViewChild(MatTable) declare table: MatTable<RequestTableItem>;

  expandedElement: RequestTableItem | null = null;

  get displayedColumns(): string[] {
    const columns = [...this.config().columns];

    if (this.config().showExpandColumn) {
      columns.push('expand');
    }

    if (this.config().showViewAdminColumn) {
      columns.push('viewAdmin');
    }

    if (this.config().showActionsColumn && this.actions().length > 0) {
      columns.push('actions');
    }

    return columns;
  }

  ngAfterViewInit() {
    if (this.sort) {
      const config = this.config();
      if (config.initialSort?.active) {
        this.sort.active = config.initialSort.active;
        this.sort.direction = config.initialSort?.direction ?? 'desc';
      }
      this.dataSource().sort = this.sort;
    }
  }

  onSortChange(event: Sort) {
    this.sortChange.emit(event);
  }

  onPageChange(event: PageEvent) {
    this.pageChange.emit(event);
  }

  onRefreshClick(): void {
    this.refresh.emit();
  }

  toggle(element: RequestTableItem): void {
    this.expandedElement = this.isExpanded(element) ? null : element;
    this.rowToggle.emit(element);
  }

  isExpanded(element: RequestTableItem): boolean {
    return this.expandedElement === element;
  }

  getStatusClass(statusCode: StatusCode): string {
    return this.config().statusClassFunction?.(statusCode) ?? '';
  }

  getConsistencyClass(isInconsistency?: boolean | null): string {
    if (isInconsistency == null) {
      return 'chip-neutral';
    }
    return isInconsistency ? 'chip-inconsistent' : 'chip-consistent';
  }

  getConsistencyLabel(isInconsistency?: boolean | null): string {
    if (isInconsistency == null) {
      return 'N/A';
    }
    return isInconsistency ? 'Inconsistente' : 'Consistente';
  }

  executeAction(
    action: RequestTableAction,
    item: RequestTableItem,
    event: Event,
  ) {
    event.stopPropagation();
    action.action(item);
  }

  shouldShowAction(
    action: RequestTableAction,
    item: RequestTableItem,
  ): boolean {
    return !action.condition || action.condition(item);
  }

  goViewAdmin(item: RequestTableItem): void {
    this.viewAdminClick.emit(item);
  }
}
